import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getKillSwitchState, type KillSwitchState } from '@/lib/api';

const switchRows: { key: keyof KillSwitchState; label: string }[] = [
  { key: 'readOnly', label: 'Read only' },
  { key: 'publication', label: 'Publication' },
  { key: 'ai', label: 'AI' },
  { key: 'driftIngest', label: 'Drift ingest' },
  { key: 'workflowDispatch', label: 'Workflow dispatch' },
];

function KillSwitchChip({ label, active }: { label: string; active: boolean }): React.ReactElement {
  return (
    <div className="flex items-center justify-between rounded-md border border-border p-3">
      <span className="text-sm font-medium">{label}</span>
      <Badge variant={active ? 'destructive' : 'outline'}>{active ? 'On' : 'Off'}</Badge>
    </div>
  );
}

export function AdminKillSwitchStatus(): React.ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: ['adminKillSwitchStatus'],
    queryFn: getKillSwitchState,
    refetchInterval: 30_000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kill Switches</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {error instanceof Error && (
          <div className="text-sm text-destructive">
            Failed to load kill-switch status: {error.message}
          </div>
        )}
        {data && (
          <div className="grid gap-2 sm:grid-cols-2">
            {switchRows.map(row => (
              <KillSwitchChip key={row.key} label={row.label} active={data[row.key]} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
